import React from "react";
import { ReactSVG } from "react-svg";
import "../style/typography.scss";
import "../style/colors.scss";
import "../style/badges.scss";
import { setSVGColor } from "../SVGHelper";

import dotIcon from "../icon/Dot.svg";

/**
 * Convenience component for displaying a badge with optional icons or default styling.
 * This component uses the styling from badges.scss.
 *
 * @note When overriding the `className` prop, ensure to include a base style from badges.scss for consistent badge presentation.
 *
 * @param {string} [props.text]                     The main text or content of the badge.
 * @param {string} [props.leftIcon=dotIcon]         SVG path for the icon on the left side of the badge text. If set to null/false, the icon is removed.
 *                                                  By default, it uses a dot.
 * @param {string} [props.rightIcon]                SVG path for the icon on the right side of the badge text.
 * @param {string} [props.className='badge-filled'] CSS class names to be added to the badge.
 * @param {object} [props.style={}]                 Inline styles to be added to the badge.
 *
 * @returns {ReactElement} The rendered Badge component.
 *
 * @example
 * <Badge text="badge" />  <- will have a dot icon on the left side as a default
 * <Badge text="Warning" leftIcon={null} className="badge-warning badge-outlined" />  <- no left dot + some styling
 */
const Badge = ({
    text,
    leftIcon = dotIcon, //this is the default but you can pass null/false to remove it
    rightIcon,
    className = "badge-filled", //if you override this make sure to include a base style from badges.scss
    style = {},
}) => {
    const iconSize = "17px"; //17 looks better then 16

    const Icon = ({ svg, style = {} }) => {
        return (
            <>
                {svg ? (
                    <ReactSVG
                        src={svg}
                        beforeInjection={(svg) => {
                            svg.setAttribute("width", iconSize);
                            svg.setAttribute("height", iconSize);
                            setSVGColor("currentColor", false, ["white"])(svg);
                        }}
                        style={style}
                    />
                ) : null}
            </>
        );
    };

    return (
        <div style={style} className={className}>
            <div>
                <Icon svg={leftIcon} style={{ marginRight: "-4px" }} />
            </div>
            {text}
            <div>
                <Icon svg={rightIcon} style={{ marginLeft: "-4px" }} />
            </div>
        </div>
    );
};

export default Badge;
